import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { Glyph } from './GameIcon';

/** After a link is sent, the button waits this long (the server sends nothing new sooner either). */
const COOLDOWN_S = 60;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Forgot password?" inside the sign-in form: an email address in, a reset link out (see POST
 * /api/auth/forgot). One tap sends one request: the button is held while it's in flight and for a
 * minute after, and the answer is the same whether or not the email has an account.
 */
export function ForgotPassword({ initialEmail = '', onBack }: { initialEmail?: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(EMAIL_RE.test(initialEmail) ? initialEmail : '');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const valid = EMAIL_RE.test(email.trim());
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    // A ref, not state: a second tap before React re-renders must not send a second request.
    if (!valid || inFlight.current || cooldown > 0) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/forgot', { body: { email: email.trim() }, auth: false });
      setSentTo(email.trim());
      setCooldown(COOLDOWN_S);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'rate_limited' ? t('auth.forgot.limit') : t('errors.network'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit} noValidate>
      <p className="faint" style={{ margin: 0 }}>{t('auth.forgot.hint')}</p>
      <div className="field">
        <label htmlFor="forgot-email">{t('auth.email')}</label>
        <input
          id="forgot-email"
          className="input"
          type="email"
          autoFocus
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email && setTouched(true)}
          aria-invalid={touched && !valid}
          aria-describedby="forgot-email-error"
          required
        />
        {touched && !valid && <span id="forgot-email-error" className="error-text">{t('auth.invalidEmail')}</span>}
      </div>
      {sentTo && <div className="notice success" role="status"><Glyph e="✉️" /> {t('auth.forgot.sent', { email: sentTo })}</div>}
      {error && <div className="error-text" role="alert">{error}</div>}
      <button className="btn primary lg block" disabled={busy || cooldown > 0}>
        {busy ? <span className="spin" /> : cooldown > 0 ? t('auth.forgot.resendIn', { s: cooldown }) : sentTo ? t('auth.forgot.resend') : t('auth.forgot.send')}
      </button>
      <button type="button" className="btn ghost block" onClick={onBack}>
        ← {t('auth.forgot.back')}
      </button>
    </form>
  );
}
